import { useMemo } from 'react'

import type {
  FilterConfig,
  FilterOptionsResult,
  FilterSelectOption,
} from '@/components/CustomFilterForm/types'
import { useAccountLocations } from '@/features/addresses/useAddress'
import { useLoadUploads } from '@/features/shipments-upload/queries'
import { useUserSettings } from '@/hooks/settings'
import type { UserSettings } from '@/hooks/settings/useUserSettings'

type UserSettingsFilterOptions = {
  enabled: boolean
  select: (
    settings: UserSettings | undefined,
  ) => FilterSelectOption[] | undefined
}
const useUserSettingsFilterOptions = ({
  enabled,
  select,
}: UserSettingsFilterOptions) => {
  const { data: settings, isLoading } = useUserSettings(enabled)
  return {
    options: select(settings) ?? [],
    isLoading,
  }
}
type FilterOptionsHook = (enabled: boolean) => FilterOptionsResult

export const useCarriersFilterOptions: FilterOptionsHook = enabled =>
  useUserSettingsFilterOptions({
    enabled,
    select: settings =>
      settings?.carriers.map(carrier => ({
        text: carrier.label,
        value: carrier._id,
      })),
  })

export const useUsersFilterOptions: FilterOptionsHook = enabled =>
  useUserSettingsFilterOptions({
    enabled,
    select: settings =>
      settings?.users.map(user => ({
        text: user.label,
        value: user._id,
      })),
  })

export const useTagsFilterOptions: FilterOptionsHook = enabled =>
  useUserSettingsFilterOptions({
    enabled,
    select: settings =>
      settings?.tags.map(tag => ({
        text: tag.label,
        value: tag._id,
      })),
  })

export const useGlCodesFilterOptions: FilterOptionsHook = enabled =>
  useUserSettingsFilterOptions({
    enabled,
    select: settings =>
      settings?.glCodes.map(code => ({
        text: code.code,
        value: code.code,
      })),
  })

export const useAccountLocationFilterOptions: FilterOptionsHook = enabled =>
  useUserSettingsFilterOptions({
    enabled,
    select: settings => {
      const removedLocationOptions =
        settings?.removedLocations?.map(location => ({
          text: `REMOVED - ${location.label}`,
          value: location._id,
        })) ?? []
      return [
        ...(settings?.locations ?? []).map(location => ({
          text: location.label,
          value: location._id,
        })),
        ...removedLocationOptions,
      ]
    },
  })

export const useVendorsFilterOptions: FilterOptionsHook = enabled =>
  useUserSettingsFilterOptions({
    enabled,
    select: settings =>
      settings?.vendors.map(vendor => ({
        text: vendor.label,
        value: vendor._id,
      })),
  })

export function useAddressBookLocationFilterOptions(
  enabled: boolean,
): FilterOptionsResult {
  const { data: accountLocations, isLoading } = useAccountLocations(enabled)

  return {
    options: (accountLocations ?? []).map(location => ({
      text: location.formatted,
      value: location._id,
    })),
    isLoading,
  }
}
export function useUploadFilterOptions(enabled: boolean): FilterOptionsResult {
  const { data, isLoading } = useLoadUploads(enabled)

  return {
    options: (data ?? []).map(upload => ({
      text: upload.name,
      value: upload._id,
    })),
    isLoading,
  }
}

export function useResolvedLazyOptions(
  activeFilters: FilterConfig[],
): Map<FilterOptionsHook, FilterSelectOption[]> {
  const usesHook = (hook: FilterOptionsHook) =>
    activeFilters.some(filter => filter.useOptions === hook)

  const carriers = useCarriersFilterOptions(usesHook(useCarriersFilterOptions))
  const users = useUsersFilterOptions(usesHook(useUsersFilterOptions))
  const tags = useTagsFilterOptions(usesHook(useTagsFilterOptions))
  const glCodes = useGlCodesFilterOptions(usesHook(useGlCodesFilterOptions))
  const locations = useAccountLocationFilterOptions(
    usesHook(useAccountLocationFilterOptions),
  )
  const vendors = useVendorsFilterOptions(usesHook(useVendorsFilterOptions))
  const uploads = useUploadFilterOptions(usesHook(useUploadFilterOptions))
  const accountLocations = useAddressBookLocationFilterOptions(
    usesHook(useAddressBookLocationFilterOptions),
  )

  return useMemo(() => {
    const resolved = new Map<FilterOptionsHook, FilterSelectOption[]>()
    resolved.set(useCarriersFilterOptions, carriers.options)
    resolved.set(useUsersFilterOptions, users.options)
    resolved.set(useTagsFilterOptions, tags.options)
    resolved.set(useGlCodesFilterOptions, glCodes.options)
    resolved.set(useAccountLocationFilterOptions, locations.options)
    resolved.set(useVendorsFilterOptions, vendors.options)
    resolved.set(useUploadFilterOptions, uploads.options)
    resolved.set(useAddressBookLocationFilterOptions, accountLocations.options)
    return resolved
  }, [
    accountLocations.options,
    carriers.options,
    glCodes.options,
    locations.options,
    tags.options,
    uploads.options,
    users.options,
    vendors.options,
  ])
}
